import React, { useRef } from 'react';

import useChangeLanguage from '@/hooks/apps/useChangeLanguage';
import useCurrentLang from '@/hooks/apps/useCurrentLang';
import useDropdown from '@/hooks/shared/useDropdown';

import theme from '@/styles/theme';

import { HeaderVariantType } from '../../layouts/pc/Header';
import {
  IconChevronDownRound,
  IconChevronUpRound,
  IconGlobeLine,
} from '../icon';

type Props = {
  variant?: HeaderVariantType;
};

export type SelectLanguageItemType = {
  key: string;
  label: string | number | React.ReactNode;
  onClick?: () => void;
  highlight?: boolean;
};

const buttonLabels = {
  ko: 'KOR',
  en: 'ENG',
};

export default function SelectLanguage({ variant = 'default' }: Props) {
  const targetRef = useRef(null);
  const [isOpen, setIOpen] = useDropdown(targetRef, false);

  function handleToggle() {
    setIOpen(!isOpen);
  }

  const currentLang = useCurrentLang();
  const changeLanguage = useChangeLanguage();
  const activeLangLabel = buttonLabels[currentLang];

  const items: SelectLanguageItemType[] = [
    {
      key: 'ko',
      label: 'KOR',
      highlight: currentLang === 'ko',
      onClick: () => {
        changeLanguage('ko');
      },
    },
    {
      key: 'en',
      label: `ENG`,
      highlight: currentLang === 'en',
      onClick: () => {
        changeLanguage('en');
      },
    },
  ];

  const handleIconColor = () => {
    if (variant === 'transparent') {
      return theme`colors.apps.coreGrayWhite`;
    }
    return theme`colors.apps.coreGrayBlack`;
  };

  return (
    <div className="relative flex w-[88px]">
      <button
        ref={targetRef}
        type="button"
        onClick={handleToggle}
        className={`inline-flex h-[34px] flex-shrink-0 items-center justify-end gap-1 rounded-lg border border-solid px-2 text-sm font-medium leading-[150%] ${
          variant === 'transparent'
            ? 'border-apps-coreGrayWhite text-apps-coreGrayWhite'
            : 'border-apps-coreProductBorder text-apps-coreGrayBlack'
        }`}
      >
        <IconGlobeLine size={16} color={handleIconColor()} />
        {activeLangLabel}
        {isOpen ? (
          <IconChevronUpRound size={16} color={handleIconColor()} />
        ) : (
          <IconChevronDownRound size={16} color={handleIconColor()} />
        )}
      </button>
      {isOpen && (
        <ul className="absolute left-0 top-[calc(100%+4px)] z-[1000] w-full rounded-xl border border-solid border-apps-coreProductDivider bg-apps-coreGrayWhite p-1 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.04)] xl:left-auto xl:right-0">
          {items?.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                onClick={item.onClick}
                className={`relative flex min-h-10 w-full cursor-pointer items-center gap-x-2.5 rounded-[5px] px-4 py-2 text-sm leading-[150%] tracking-[-0.28px] hover:[@media(hover:hover)]:bg-apps-coreGray07 ${
                  item.highlight
                    ? 'text-apps-corePrimary01'
                    : 'text-apps-coreGrayBlack'
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
